import type { ReactNode } from "react";
import { LayoutGrid, List, Loader2, Search } from "lucide-react";
import type { DentistStatusBucket } from "./dentist-status";
import { PilulasDeStatus, ProsthesisStatusFilterMenu } from "./ProsthesisStatusFilterMenu";
import { OrdenarPedidos } from "./OrdenarPedidos";
import type { OrderSortField, SortDirection } from "./prosthesis-orders-sort";

type Props = {
  busca: string;
  onBusca: (v: string) => void;
  /** O botão Todos / Por profissional, quando a pessoa pode ver os dois. */
  lente?: ReactNode;
  status: {
    selecionados: DentistStatusBucket[];
    contagem: Record<DentistStatusBucket, number>;
    total: number;
    onAlternar: (s: DentistStatusBucket) => void;
    onLimpar: () => void;
  };
  ordem: { campo: OrderSortField; direcao: SortDirection; onChange: (c: OrderSortField, d: SortDirection) => void };
  visualizacao: "card" | "list";
  onVisualizacao: (v: "card" | "list") => void;
  carregando: boolean;
  /** Na linha da busca — o filtro de organizações no acesso pessoal. */
  extra?: ReactNode;
};

/**
 * Filtros da lista de pedidos no celular, em duas linhas: busca + modo; Status + Ordenar +
 * visualização. Antes eram cinco faixas empilhadas antes do primeiro pedido.
 */
export function BarraDePedidosCelular({ busca, onBusca, lente, status, ordem, visualizacao, onVisualizacao, carregando, extra }: Props) {
  const emCards = visualizacao === "card";
  return (
    <div className="mb-3 space-y-2">
      <div className="flex items-center gap-2">
        <div className="relative min-w-0 flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
          <input
            type="search"
            value={busca}
            onChange={(e) => onBusca(e.target.value)}
            placeholder="Buscar paciente, lab, material..."
            className="h-11 w-full rounded-md border border-zinc-200 bg-white pl-9 pr-3 text-base text-zinc-900 placeholder:text-zinc-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-border dark:bg-card dark:text-foreground"
          />
          {carregando && <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-zinc-400" />}
        </div>
        {extra}
        {lente}
      </div>

      <div className="flex items-center gap-2">
        <ProsthesisStatusFilterMenu
          selecionados={status.selecionados}
          contagemPorStatus={status.contagem}
          total={status.total}
          onAlternar={status.onAlternar}
          onLimpar={status.onLimpar}
          className="w-auto shrink-0"
        />
        <OrdenarPedidos compacto campo={ordem.campo} direcao={ordem.direcao} onChange={ordem.onChange} className="min-w-0 flex-1" />
        {/* Ícone da visão ATUAL (o padrão de Gmail/Drive): olhar o botão diz o que está na tela. */}
        <button
          type="button"
          data-tour="protese-visualizacao"
          onClick={() => onVisualizacao(emCards ? "list" : "card")}
          aria-label={emCards ? "Vendo em cards — tocar pra ver em lista" : "Vendo em lista — tocar pra ver em cards"}
          title={emCards ? "Cards" : "Lista"}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-50 dark:border-border dark:bg-card dark:text-foreground"
        >
          {emCards ? <LayoutGrid className="h-5 w-5" /> : <List className="h-5 w-5" />}
        </button>
      </div>

      <PilulasDeStatus selecionados={status.selecionados} onAlternar={status.onAlternar} />
    </div>
  );
}
